'use client'

import { Minus, Plus, Trash2, Gift, CirclePercent, Medal } from 'lucide-react'
import { useCartItem } from '@/lib/commerce/hooks'
import { formatPrice } from '@/utils/util'
import { navigateUrl } from '@/utils/client-request'

export default function CartItemSidebar({ product, pathname }: { product: any, pathname: string }) {
  const c = useCartItem(product)

  return (
    <div className="grid grid-cols-[64px_1fr_auto] gap-3.5 py-4 border-b border-dashed border-foreground/10 last:border-0">
      <a href={navigateUrl(`/product/${c.productCustomId}`, pathname)} className="relative shrink-0 block w-16 h-16">
        <div className="w-16 h-16 rounded-2xl overflow-hidden bg-[#F1EEE7]">
          <img src={c.imageUrl} alt={c.productName} className="w-full h-full object-cover" />
        </div>
        {c.isReward && (
          <span className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-[#FF7AA8] text-white inline-flex items-center justify-center shadow-sm ring-2 ring-cream z-10">
            <Gift className="size-3" strokeWidth={2.5} />
          </span>
        )}
      </a>

      <div className="min-w-0">
        <a href={navigateUrl(`/product/${c.productCustomId}`, pathname)} className="block">
          <div className="text-[14px] font-semibold leading-[1.3] text-foreground line-clamp-2">{c.productName}</div>
        </a>
        {(c.showVariantName || c.dimension) && (
          <div className="mt-1 text-[12px] text-foreground/55 truncate">
            {[c.showVariantName ? c.variant?.name : null, c.dimension].filter(Boolean).join(' · ')}
          </div>
        )}
        {c.showDiscountedPrice && c.discountName && (
          <span className={`mt-1.5 inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full ${c.isReward ? 'bg-green-100 text-green-700' : 'bg-purple-100 text-purple-700'}`}>
            {c.isReward ? <Gift className="size-3" strokeWidth={2.5} /> : <CirclePercent className="size-3" strokeWidth={2.5} />}
            <span className="line-clamp-1">{c.discountName}</span>
          </span>
        )}
        {c.loyaltyDeal && (
          <span className="mt-1.5 inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full bg-violet-100 text-violet-700 whitespace-nowrap">
            <Medal className="size-3" strokeWidth={2.5} /> Лояалти −{c.loyaltyDeal.percent}%
          </span>
        )}

        <div className="mt-2.5">
          {c.isReward ? (
            <div className="inline-flex items-center h-8 px-3.5 rounded-full border border-foreground/10 bg-white">
              <span className="min-w-4 text-center text-[13px] font-semibold text-foreground tabular-nums">{c.qty}</span>
            </div>
          ) : c.isOutOfStock ? (
            <span className="inline-flex items-center h-7 px-3 rounded-full bg-red-50 text-[12px] font-semibold text-red-600">Дууссан</span>
          ) : (
            <div className="inline-flex items-center h-8 px-1 rounded-full border border-foreground/10 bg-white">
              <button onClick={c.handleRemoveOne} disabled={c.loading} className="w-6 h-6 inline-flex items-center justify-center rounded-full text-foreground hover:bg-foreground/5 disabled:opacity-40 disabled:cursor-not-allowed transition-colors cursor-pointer">
                <Minus className="size-3.5" />
              </button>
              <span className="min-w-4 text-center text-[13px] font-semibold text-foreground px-1.5">{c.qty}</span>
              <button onClick={c.handleAddOne} disabled={c.loading} className="w-6 h-6 inline-flex items-center justify-center rounded-full text-foreground hover:bg-foreground/5 disabled:opacity-40 disabled:cursor-not-allowed transition-colors cursor-pointer">
                <Plus className="size-3.5" />
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="flex flex-col items-end gap-2 shrink-0">
        <div className="text-[14px] font-semibold text-foreground">
          {c.unitPrice === 0 ? 'Үнэгүй' : formatPrice(c.displayPrice)}
        </div>
        {c.showDiscountedPrice && c.originalPrice && (
          <div className="text-[11px] line-through text-foreground/40">{formatPrice(c.qty * c.originalPrice)}</div>
        )}
        <button onClick={c.handleRemove} disabled={c.loading} aria-label="Хасах" className="w-7 h-7 inline-flex items-center justify-center rounded-full text-foreground/55 hover:text-foreground hover:bg-foreground/5 disabled:opacity-40 transition-colors cursor-pointer">
          <Trash2 className="size-3.5" />
        </button>
      </div>
    </div>
  )
}
